import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  /** When false (default), number inputs reject negatives and block scroll-wheel changes. */
  allowNegative?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      allowNegative = false,
      min,
      onWheel,
      onKeyDown,
      onChange,
      ...props
    },
    ref
  ) => {
    const isNumber = type === "number";

    const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
      if (isNumber) {
        event.currentTarget.blur();
      }
      onWheel?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isNumber && !allowNegative && ["-", "e", "E", "+"].includes(event.key)) {
        event.preventDefault();
      }
      onKeyDown?.(event);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumber && !allowNegative && event.target.value !== "") {
        const parsed = Number(event.target.value);
        if (!Number.isNaN(parsed) && parsed < 0) {
          event.target.value = "0";
        }
      }
      onChange?.(event);
    };

    const resolvedMin =
      isNumber && !allowNegative && min === undefined ? 0 : min;

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        min={resolvedMin}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
